/**
 * Exercise plans, from the server — a client's, or the whole workspace's —
 * and the writes that change them.
 *
 * The page is the server's: the panel asks for one page's worth and gets
 * exactly that, plus the `count` of everything the filter matches, which is
 * what it divides into pages to draw the control with. Turning a page is
 * therefore another call.
 *
 * Which page is the caller's to hold, not this hook's: it is the thing the
 * panel has to reset when a plan is written, and the hook has no way of
 * knowing that a change came from the user rather than from a re-render.
 *
 * Every write refreshes what was fetched. A create and a save both answer with
 * the plan, but neither answers with the page it now belongs on — the name and
 * the number are the backend's, and so is the order — so the list is asked
 * again rather than patched from the response.
 */

import { useCallback, useSyncExternalStore } from "react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
// The Create plan screen's totals count this kind too, and a plan written
// here without leaving that screen has to move the figure above it.
import { PLAN_COUNTS_QUERY_KEY } from "../../mealPlans/hooks/usePlanCounts";
import {
  DEFAULT_PAGE_SIZE,
  createExercisePlan,
  fetchExercisePlans,
  sendExercisePlan,
  updateExercisePlan,
} from "../services/exercisePlansService";
import {
  getDeletedExercisePlanIds,
  hideExercisePlan,
  subscribeToExercisePlans,
} from "../services/exercisePlanStore";
import type {
  ExerciseEntry,
  ExercisePlanRecord,
  ExercisePlanSummary,
} from "../types/exercisePlans.types";

/** Everything this module caches, for the writes that have to clear it. */
export const EXERCISES_QUERY_KEY = ["exercises"] as const;

export type ExercisePlansStatus = "loading" | "ready" | "failed";

export interface ExercisePlansState {
  /** One page of them — `pageSize` rows at most, not the whole list. */
  plans: ExercisePlanSummary[];
  /** How many the server holds for this client, not how many came back. */
  count: number;
  status: ExercisePlansStatus;
  /** Set only when `status` is `failed` — what the panel shows. */
  error?: string;
  /**
   * Whether the server refused the read outright rather than failing to
   * answer it — a 403, which is what a workspace with no plan running gets.
   * Not an error the reader can do anything about, and not news either: a
   * practice that has never been able to write an exercise plan has never
   * written one. The panel draws it as the empty list it is.
   */
  refused: boolean;
  /**
   * Writes a new plan and hands back what the backend made of it. Rejects if
   * the request does, leaving the dialog to report it — nothing is shown as
   * written unless the server said so.
   *
   * `clientId` is who it is for. Left off, it is the `client` the list was
   * asked about — which a workspace-wide list has none of, so the panel that
   * lists everyone's names the client it picked.
   */
  create: (
    entries: ExerciseEntry[],
    clientId?: string
  ) => Promise<ExercisePlanRecord>;
  /** Rewrites one, whole. Rejects the same way. */
  save: (
    plan: ExercisePlanSummary,
    entries: ExerciseEntry[]
  ) => Promise<ExercisePlanRecord>;
  /** Sends it, and hands back the backend's own word on what happened. */
  send: (planId: string, emails: string[]) => Promise<string | undefined>;
  /**
   * Hides one. Not a delete: there is no endpoint for that, so the plan is
   * dropped from what this browser draws and comes back on a refresh.
   */
  hide: (planId: string) => void;
  reload: () => void;
}

export interface UseExercisePlansOptions {
  /**
   * A client's `public_id` — whose plans are asked for. Absent asks for the
   * whole workspace, which is what the Create plan screen lists.
   */
  client?: string;
  /** 1-based, the way the endpoint counts. Changing it refetches. */
  page?: number;
  pageSize?: number;
}

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

const NO_PLANS: ExercisePlanSummary[] = [];

export function useExercisePlans({
  client,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseExercisePlansOptions): ExercisePlansState {
  const getToken = useTokenGetter();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      ...EXERCISES_QUERY_KEY,
      "list",
      client ?? null,
      page,
      pageSize,
    ] as const,
    queryFn: ({ signal }) =>
      fetchExercisePlans(getToken, { client, page, pageSize, signal }),
    // The rows already on screen stay while the next page is fetched — cached
    // across mounts too, so a tab switched away from and back shows its last
    // page instantly while a fresh read runs behind it.
    placeholderData: keepPreviousData,
  });

  /**
   * Everything this module has cached, thrown away — and the Create plan
   * screen's totals with it, since one of the three is this kind.
   *
   * The whole key and not just the list: a plan that was rewritten has a
   * detail cached under it too, and leaving that behind would reopen the plan
   * on the lines it had before the save.
   */
  const refresh = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: EXERCISES_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: PLAN_COUNTS_QUERY_KEY }),
      ]).then(() => undefined),
    [queryClient]
  );

  const create = useCallback(
    async (entries: ExerciseEntry[], clientId = client) => {
      // Not an ApiError: the server was never asked. A create with nobody to
      // write for is a bug at the call site, not something to show a user.
      if (!clientId) throw new Error("An exercise plan needs a client.");

      const plan = await createExercisePlan(getToken, { clientId, entries });
      await refresh();
      return plan;
    },
    [getToken, client, refresh]
  );

  const save = useCallback(
    async (plan: ExercisePlanSummary, entries: ExerciseEntry[]) => {
      // The plan's own client rather than the one this tab is about. They are
      // the same client every time the panel calls it, and the payload is the
      // plan's — so it is taken from the plan, which is the thing being saved.
      const saved = await updateExercisePlan(getToken, plan.id, {
        clientId: plan.clientId || client || "",
        entries,
      });
      await refresh();
      return saved;
    },
    [getToken, client, refresh]
  );

  const send = useCallback(
    async (planId: string, emails: string[]) => {
      const message = await sendExercisePlan(getToken, planId, emails);
      // `last_sent_at` moves on the record, which the row reads.
      await refresh();
      return message;
    },
    [getToken, refresh]
  );

  const refused = query.error instanceof ApiError && query.error.status === 403;

  return {
    // Back to nothing on a real failure: a row from a page that is no longer
    // confirmed current is worse than none.
    plans: query.isError ? NO_PLANS : query.data?.plans ?? NO_PLANS,
    count: query.isError ? 0 : query.data?.count ?? 0,
    status: query.isError ? "failed" : query.isFetching ? "loading" : "ready",
    error: query.isError && !refused ? failureMessage(query.error) : undefined,
    refused: query.isError && refused,
    create,
    save,
    send,
    hide: hideExercisePlan,
    reload: () => {
      void query.refetch();
    },
  };
}

/**
 * The plans hidden in this sitting. The server has not been told — there is no
 * endpoint for that — so it keeps returning them, and the panel drops them
 * from what it fetched rather than showing a row the user was just told had
 * gone.
 */
export function useDeletedExercisePlanIds(): string[] {
  return useSyncExternalStore(
    subscribeToExercisePlans,
    getDeletedExercisePlanIds,
    getDeletedExercisePlanIds
  );
}
